import * as React from 'react';
import { observer } from 'mobx-react';
import {action, computed, makeObservable, observable, reaction, runInAction} from 'mobx';
import { FixedSizeList, ListChildComponentProps } from 'react-window';
import {
    ColumnFilters,
    ColumnSort, FilterOption,
    FilterPopup,
    FilterValue
} from './FilterPopup/FilterPopup';
import { FaFilter, FaUndo } from 'react-icons/fa';
import { TiArrowSortedUp, TiArrowSortedDown } from 'react-icons/ti';
import {DataFieldType} from '@Components';

import './_dataGrid.scss';
import {FilterTableStore} from './FilterPopup/FilterTableStore';
import {CrossTableHoverStore} from '@Components/DataGrid';
import {DateTimeService, TextMatchComparer, gridStringSearchService} from '@Services';
import {buildDateGroupOptions, compareValues} from '@Helpers';

const ITEM_SIZE = 25;
const HEADER_HEIGHT = 60;
const AUTO_MAX_HEIGHT = 500;

let _cachedScrollbarWidth: number | null = null;

function getScrollbarWidth(): number {
    if (_cachedScrollbarWidth !== null) return _cachedScrollbarWidth;

    const outer = document.createElement('div');
    outer.style.position = 'absolute';
    outer.style.top = '-9999px';
    outer.style.width = '100px';
    outer.style.height = '100px';
    outer.style.overflow = 'scroll';
    document.body.appendChild(outer);

    const inner = document.createElement('div');
    inner.style.width = '100%';
    inner.style.height = '100%';
    outer.appendChild(inner);

    _cachedScrollbarWidth = outer.offsetWidth - inner.offsetWidth;
    outer.remove();
    return _cachedScrollbarWidth;
}

export type DataGridColumnDef<T, K extends keyof T = keyof T> = {
    title: string;
    dataField: keyof T;
    dataFieldType?: DataFieldType;
    minWidth?: string;
    maxWidth?: string;
    renderCell?: (value: T[K], item: T) =>  React.ReactNode;
    disableSorting?: boolean;
    disableFiltering?: boolean;
    show?: boolean;
    className?: string;
    getFilterOptionName?: (item: T) => string;
    getFilterItemOptionName?: (element: unknown) => string;
    renderHeader?: () => React.ReactNode;
    renderSubTitle?: () => React.ReactNode;
};

type RowData<T> = {
    items: T[];
    selectedRowId?: string;
    hoveredRowId?: string;
    columnWidthsVersion: number;
    renderText: (
        text: string | undefined,
        searchTerms?: string[],
        nodElement?: React.ReactNode
    ) => React.ReactNode;
};

type DataGridProps<T> = {
    tableKey: string;
    columns: DataGridColumnDef<T>[];
    items: T[];
    initialItemsList: T[];
    filteredItemsWithoutColumnFilters: T[];
    idField?: keyof T;
    minWidth?: string;
    maxHeight?: string;
    disableSelection?: boolean;
    totalRow?: React.ReactNode[];
    onRowClick?: (item: T) => void;
    getRowAttributes?: (item: T) => Record<string, string>;
    renderRowOverlay?: (item: T) => React.ReactNode;
    onGetRowSettings?: (item: T) => {
        style?: { backgroundColor: string };
    };
    scrollTop?: number;
    onScroll?: (scrollTop: number) => void;
    headerHeight?: number;
    onHeaderHeightChange?: (height: number) => void;
    selectedRowId?: string;
    crossTableHoverStore?: CrossTableHoverStore;
    onFilteredItemsChange?: (items: T[]) => void;
    onOwnFilteredItemsChange?: (items: T[]) => void;
    onColumnFiltersActiveChange?: (isActive: boolean) => void;
    loading?: boolean;
    totalRowAdditionalHeight?: number;
    onHorizontalScrollbarHeightChange?: (height: number) => void;
    dynamicRowHeight?: boolean;
};

@observer
export class DataGrid<T> extends React.Component<DataGridProps<T>, {}> {
    private listRef = React.createRef<FixedSizeList<RowData<T>>>();
    private containerRef = React.createRef<HTMLDivElement>();
    private horizontalScrollRef = React.createRef<HTMLDivElement>();
    private _tableRef = React.createRef<HTMLDivElement>();
    private _headerRef = React.createRef<HTMLDivElement>();
    private rowsWrapperRef = React.createRef<HTMLDivElement>();
    @observable private containerHeight = 0;
    @observable private _rowsWrapperHeight = 0;
    private _rowsWrapperObserved = false;
    private _rowRefs = new Map<string, HTMLDivElement>();
    @observable private _measuredHeaderHeight = HEADER_HEIGHT;
    @observable private _measuredHeaderScrollWidth = 0;
    private _tableFilterStore;
    @observable private _columnWidths: Map<string, number> = new Map();
    @observable private _columnWidthsVersion = 0;
    @observable private _isResizing = false;
    @observable.ref private _items: T[] = [];
    private _resizingField: string | null = null;
    private _resizeStartX = 0;
    private _resizeStartWidth = 0;
    private _headerCellRefs = new Map<string, HTMLDivElement>();
    private _filteredItemsReactionDisposer: (() => void) | null = null;
    private _ownFilteredItemsReactionDisposer: (() => void) | null = null;
    private _filtersActiveReactionDisposer: (() => void) | null = null;
    private _prevItemData: RowData<T> | null = null;
    private _layoutRO: ResizeObserver | null = null;
    private _prevScrollbarHeight = -1;

    constructor(props: DataGridProps<T>){
        super(props);
        makeObservable(this);
        this._tableFilterStore = props.tableKey
            ? FilterTableStore.getOrCreate(props.tableKey)
            : new FilterTableStore();

        if (this._tableFilterStore.columnWidths.size > 0) {
            this._columnWidths = new Map(this._tableFilterStore.columnWidths);
        }
    }

    private _getId(item: T): string {
        const field = (this.props.idField ?? 'id') as keyof T;
        return (item as Record<keyof T, unknown>)[field] as string;
    }

    private _getTerms(item: T): string[] | undefined {
        return (item as Record<string, unknown>).terms as string[] | undefined;
    }

    componentDidUpdate(prevProps: DataGridProps<T>) {
        if (prevProps.items !== this.props.items) {
            runInAction(() => { this._items = this.props.items; });

            const selectedRowId = this.props.selectedRowId ?? this._tableFilterStore.selectedRow;
            if (!prevProps.items.length && this.props.items.length && selectedRowId) {
                setTimeout(() => this._scrollToRowId(selectedRowId, 'center'), 0);
            }
        }
        if (prevProps.scrollTop !== this.props.scrollTop && this.props.scrollTop !== undefined) {
            this._scrollToOffset(this.props.scrollTop);
        }
        if (prevProps.selectedRowId !== this.props.selectedRowId) {
            this._tableFilterStore.setSelectedRow(this.props.selectedRowId);
            this.props.crossTableHoverStore?.setSelectedRowId(this.props.selectedRowId);
            if (this.props.selectedRowId) {
                this._scrollToRowId(this.props.selectedRowId);
            }
        }
        if (prevProps.columns !== this.props.columns) {
            this._measureHeaderScrollWidth();
        }
        this._ensureRowsWrapperObserved();
        this._reportScrollbarHeight();
    }

    public scrollTo(top: number) {
        this._scrollToOffset(top);
    }

    private _scrollToOffset(top: number) {
        if (this.props.dynamicRowHeight) {
            if (this.containerRef.current) this.containerRef.current.scrollTop = top;
            return;
        }
        this.listRef.current?.scrollTo(top);
    }

    private _scrollToRowId(rowId: string, align: 'smart' | 'center' = 'smart') {
        if (this.props.dynamicRowHeight) {
            this._rowRefs.get(rowId)?.scrollIntoView({ block: align === 'center' ? 'center' : 'nearest' });
            return;
        }
        const index = this.filteredItems.findIndex(p => this._getId(p) === rowId);
        if (index >= 0) this.listRef.current?.scrollToItem(index, align);
    }

    private _ensureRowsWrapperObserved() {
        if (!this.props.dynamicRowHeight || this._rowsWrapperObserved || !this.rowsWrapperRef.current || !this._layoutRO) return;
        this._layoutRO.observe(this.rowsWrapperRef.current);
        this._rowsWrapperObserved = true;
    }

    componentWillUnmount() {
        this._filteredItemsReactionDisposer?.();
        this._ownFilteredItemsReactionDisposer?.();
        this._filtersActiveReactionDisposer?.();
        this._layoutRO?.disconnect();
        document.removeEventListener('mousemove', this._onResizeMove);
        document.removeEventListener('mouseup', this._onResizeEnd);
    }

    componentDidMount() {
        runInAction(() => { this._items = this.props.items; });
        this._initLayoutObserver();

        if (this.props.selectedRowId) {
            this._tableFilterStore.setSelectedRow(this.props.selectedRowId);
            this.props.crossTableHoverStore?.setSelectedRowId(this.props.selectedRowId);
        }

        if (this.props.onColumnFiltersActiveChange) {
            this._filtersActiveReactionDisposer = reaction(
                () => !!(this._tableFilterStore.columnFilters?.size || this._tableFilterStore.columnSort !== null),
                (isActive) => this.props.onColumnFiltersActiveChange?.(isActive),
                { fireImmediately: true }
            );
        }

        const filteredItemsEquals = (a: T[], b: T[]) =>
            a.length === b.length && a.every((item: T, i: number) => item === b[i]);

        if (this.props.onFilteredItemsChange) {
            this._filteredItemsReactionDisposer = reaction(
                () => this.filteredItems,
                (items) => this.props.onFilteredItemsChange?.(items),
                { fireImmediately: true, equals: filteredItemsEquals }
            );
        }

        if (this.props.onOwnFilteredItemsChange) {
            this._ownFilteredItemsReactionDisposer = reaction(
                () => this.ownFilteredItems,
                (items) => this.props.onOwnFilteredItemsChange?.(items),
                { fireImmediately: true, equals: filteredItemsEquals }
            );
        }

        const { selectedRow } = this._tableFilterStore;

        if (selectedRow) {
            setTimeout(() => this._scrollToRowId(selectedRow, 'center'), 0);
        }

        this._ensureRowsWrapperObserved();
    }

    private _initLayoutObserver() {
        if (this._layoutRO) return;
        this._layoutRO = new ResizeObserver(entries => this._handleLayoutResize(entries));
        if (this.containerRef.current) this._layoutRO.observe(this.containerRef.current);
        if (this._headerRef.current) this._layoutRO.observe(this._headerRef.current);
        if (this.horizontalScrollRef.current) this._layoutRO.observe(this.horizontalScrollRef.current);
    }

    private _handleLayoutResize(entries: ResizeObserverEntry[]) {
        runInAction(() => {
            for (const entry of entries) {
                if (entry.target === this.containerRef.current) {
                    this.containerHeight = entry.contentRect.height;
                } else if (entry.target === this._headerRef.current) {
                    const h = this._measureNaturalHeaderHeight();
                    if (h > 0 && h !== this._measuredHeaderHeight) {
                        this._measuredHeaderHeight = h;
                        this.props.onHeaderHeightChange?.(h);
                    }
                    this._syncHeaderScrollWidth();
                } else if (entry.target === this.rowsWrapperRef.current) {
                    this._rowsWrapperHeight = entry.contentRect.height;
                } else if (entry.target === this.horizontalScrollRef.current) {
                    this._reportScrollbarHeight();
                    this._syncHeaderScrollWidth();
                }
            }
        });
    }

    // A grid's rows may not fill all the vertical space its (fixed-height) container was given —
    // in that case the total row sits above a blank gap rather than right next to the horizontal
    // scrollbar at the bottom of the container.
    private _fitsWithoutScroll(): boolean {
        if (this.props.maxHeight === 'auto') return false;
        if (this.containerHeight <= 0) return false;
        const rowsContentHeight = this.props.dynamicRowHeight ? this._rowsWrapperHeight : this.filteredItems.length * ITEM_SIZE;
        return rowsContentHeight <= this.containerHeight;
    }

    private _reportScrollbarHeight() {
        const el = this.horizontalScrollRef.current;
        if (!el) return;
        const rawHeight = el.offsetHeight - el.clientHeight;
        const h = this._fitsWithoutScroll() ? 0 : rawHeight;
        if (h !== this._prevScrollbarHeight) {
            this._prevScrollbarHeight = h;
            this.props.onHorizontalScrollbarHeightChange?.(h);
        }
    }

    private _measureNaturalHeaderHeight(): number {
        const el = this._headerRef.current;
        if (!el) return 0;
        const prevMinHeight = el.style.minHeight;
        el.style.minHeight = '0px';
        const natural = el.getBoundingClientRect().height;
        el.style.minHeight = prevMinHeight;
        return natural;
    }

    private _measureNaturalHeaderScrollWidth(): number {
        const headerEl = this._headerRef.current;
        const tableEl = this._tableRef.current;
        if (!headerEl) return 0;
        const prevMinWidth = tableEl?.style.minWidth;
        const prevWidth = tableEl?.style.width;
        if (tableEl) {
            tableEl.style.minWidth = '0px';
            tableEl.style.width = '0px';
        }
        const natural = headerEl.scrollWidth;
        if (tableEl) {
            tableEl.style.minWidth = prevMinWidth ?? '';
            tableEl.style.width = prevWidth ?? '';
        }
        return natural;
    }

    private _syncHeaderScrollWidth() {
        const scrollWidth = this._measureNaturalHeaderScrollWidth();
        if (scrollWidth !== this._measuredHeaderScrollWidth) {
            this._measuredHeaderScrollWidth = scrollWidth;
        }
    }

    private _measureHeaderScrollWidth() {
        runInAction(() => this._syncHeaderScrollWidth());
    }

    private _renderText = (text: string | undefined, searchTerms?: string[], nodElement?: React.ReactNode) => {
        if (!searchTerms) return nodElement || text;
        const parts = gridStringSearchService.markMatches(text, searchTerms, this.textComparer);
        return (
            <>
                {parts.map((p, idx) =>
                    p.isMatch ? <span className="text-match" key={idx}>{p.text}</span> : p.text
                )}
            </>
        );
    };

    private _getItemData(itemsList: T[]): RowData<T> {
        const selectedRowId = this.props.crossTableHoverStore?.selectedRowId ?? this._tableFilterStore.selectedRow;
        const hoveredRowId = this.props.crossTableHoverStore?.hoveredRowId;
        const columnWidthsVersion = this._columnWidthsVersion;
        const prev = this._prevItemData;

        if (prev && prev.items === itemsList && prev.selectedRowId === selectedRowId
            && prev.hoveredRowId === hoveredRowId && prev.columnWidthsVersion === columnWidthsVersion) {
            return prev;
        }

        const next: RowData<T> = { items: itemsList, selectedRowId, hoveredRowId, columnWidthsVersion, renderText: this._renderText };
        this._prevItemData = next;
        return next;
    }

    render() {
        const itemsList = this.filteredItems || [];
        const itemData = this._getItemData(itemsList);

        const isAutoHeight = this.props.maxHeight === 'auto';
        const hasActiveFilters = (this._tableFilterStore?.columnFilters?.size || this._tableFilterStore?.columnSort !== null);
        const hasVerticalScroll = this.props.dynamicRowHeight
            ? this._rowsWrapperHeight > this.containerHeight
            : isAutoHeight
                ? itemsList.length * ITEM_SIZE > AUTO_MAX_HEIGHT
                : itemsList.length * ITEM_SIZE > this.containerHeight;
        // When the rows don't fill the available flex space, shrink the container to fit them
        // so the total row sits right below the last row instead of leaving empty space.
        const fitsWithoutScroll = this._fitsWithoutScroll();
        const rowsContentHeight = this.props.dynamicRowHeight ? this._rowsWrapperHeight : itemsList.length * ITEM_SIZE;
        const listHeight = isAutoHeight
            ? Math.min(itemsList.length * ITEM_SIZE, AUTO_MAX_HEIGHT)
            : fitsWithoutScroll ? rowsContentHeight : this.containerHeight;
        const totalWidth = this._totalContentWidth;
        const effectiveMinWidth = this._measuredHeaderScrollWidth > 0 ? `${this._measuredHeaderScrollWidth}px` : this.props.minWidth;
        const listWidth: number | string = totalWidth ?? '100%';

        const tableStyle: React.CSSProperties = totalWidth != null
            ? { width: `${totalWidth}px`, minWidth: 0 }
            : { minWidth: effectiveMinWidth };
        const containerStyle: React.CSSProperties = {
            ...(totalWidth != null
                ? { width: `${totalWidth}px`, minWidth: 0 }
                : { width: '100%', minWidth: effectiveMinWidth }),
            ...(fitsWithoutScroll ? { flex: '0 0 auto' } : undefined)
        };
        const containerHeightStyle = isAutoHeight
            ? `${listHeight}px`
            : fitsWithoutScroll ? `${rowsContentHeight}px` : undefined;
        const hasNoData = !this._items?.length && !this.props.loading;
        const gridHeightStyle = hasNoData ? undefined : this.props.maxHeight;

        const showScrollbarSpacer = this.props.dynamicRowHeight || hasVerticalScroll;

        return (
            <div className={`data-grid${this._isResizing ? ' is-resizing' : ''}${this.props.dynamicRowHeight ? ' dynamic-grid' : ''}`} style={{ height: gridHeightStyle }} onMouseLeave={() => this.props.crossTableHoverStore?.setHoveredRowId(undefined)}>
                <div className="grid-toolbar-icons">
                    {hasActiveFilters && <FaFilter className="clear-filters-icon" title="Clear table filters" onClick={this._onClearFilters} />}
                    {this._columnWidths.size > 0 && <FaUndo className="reset-columns-icon" title="Reset column widths" onClick={this._onResetColumnWidths} />}
                </div>
                {this.props.loading && (
                    <div className="data-grid-loader">
                        <div className="spinner-border spinner-border-sm" role="status" />
                    </div>
                )}
                <div className="data-horizontal-scroll" ref={this.horizontalScrollRef}>
                    <div className="data-grid-table" ref={this._tableRef} style={tableStyle}>
                        <div
                            className="data-table-header"
                            ref={this._headerRef}
                            style={{
                                ...(this.props.headerHeight ? { minHeight: `${this.props.headerHeight}px` } : undefined),
                                paddingRight: showScrollbarSpacer && !totalWidth ? `${this._getScrollbarWidth()}px` : undefined
                            }}
                        >
                            {this.props.columns.map((column, idx) => this._renderHeaderCell(
                                column,
                                showScrollbarSpacer && !totalWidth && idx === this.props.columns.length - 1
                            ))}
                        </div>
                    </div>

                    <div
                        ref={this.containerRef}
                        className={`data-table-container ${!this._items?.length ? 'no-content' : ''}`}
                        style={{ height: containerHeightStyle, ...containerStyle }}
                        onScroll={this.props.dynamicRowHeight ? (e) => this.props.onScroll?.(e.currentTarget.scrollTop) : undefined}
                    >
                        {this._items?.length > 0 ? (
                            this.props.dynamicRowHeight ? (
                                <div ref={this.rowsWrapperRef} className="data-table-rows-dynamic" style={{ width: listWidth }}>
                                    {itemsList.map((item, index) => this._renderDynamicRow(item, index, itemData))}
                                </div>
                            ) : (
                                <>
                                    {listHeight > 0 && (
                                        <FixedSizeList
                                            ref={this.listRef}
                                            height={listHeight}
                                            width={listWidth}
                                            itemCount={itemsList.length}
                                            itemSize={ITEM_SIZE}
                                            itemData={itemData}
                                            overscanCount={20}
                                            itemKey={(index, data) => this._getId(data.items[index])}
                                            onScroll={({ scrollOffset, scrollUpdateWasRequested }) => {
                                                if (!scrollUpdateWasRequested) this.props.onScroll?.(scrollOffset);
                                            }}
                                        >
                                            {this._renderRow}
                                        </FixedSizeList>
                                    )}
                                </>
                            )
                        ) : !this.props.loading ? (
                            <div className="no-data-block">No row data</div>
                        ) : null}
                    </div>

                    {this.props.totalRow && !!this._items?.length && (
                        <>
                            <div
                                className="total-row"
                                style={{
                                    ...tableStyle,
                                    paddingRight: showScrollbarSpacer ? `${this._getScrollbarWidth()}px` : undefined
                                }}
                            >
                                {this.props.columns.map((column, idx) => {
                                    const cellValue = this.props.totalRow?.[idx] ?? null;
                                    return (
                                        <div
                                            key={column.dataField as string}
                                            className={cellValue === null ? 'total-row-cell-empty' : undefined}
                                            style={this._getColumnStyle(column)}
                                        >
                                            {cellValue}
                                        </div>
                                    );
                                })}
                            </div>
                            {!!this.props.totalRowAdditionalHeight && (
                                <div className="total-row-spacer" style={{ height: `${this.props.totalRowAdditionalHeight}px` }} />
                            )}
                        </>
                    )}
                </div>
            </div>
        );
    }

    private _getRowClassName(item: T, itemId: string, data: RowData<T>, index: number, extraClass?: string): string {
        const isSelectedRow = !this.props?.disableSelection && (
            data.selectedRowId !== undefined
                ? data.selectedRowId === itemId
                : this._tableFilterStore.selectedRow === itemId
        );
        const isHighlightedRow = data.hoveredRowId !== undefined && data.hoveredRowId === itemId;
        const isAltRow = index % 2 === 1;

        return `data-table-content${extraClass ? ' ' + extraClass : ''}${isSelectedRow ? ' selected' : ''}${isHighlightedRow ? ' highlighted' : ''}${isAltRow ? ' alt-row' : ''}${this.props.disableSelection ? ' disable-hover' : ''}`;
    }

    private _handleRowClick = (item: T, itemId: string) => {
        if (this.props.disableSelection) return;
        this._tableFilterStore?.setSelectedRow(itemId);
        this.props.crossTableHoverStore?.setSelectedRowId(itemId);
        this.props?.onRowClick?.(item);
    };

    private _renderRowCells(item: T, data: RowData<T>) {
        return this.props.columns.map((column) => (
            <div
                key={column.dataField as string}
                style={{...this._getColumnStyle(column), background: this.props.onGetRowSettings?.(item)?.style?.backgroundColor}}
                className={column.className}
            >
                {column.renderCell ? column.renderCell(item[column.dataField], item)
                    : item[column.dataField] ? data.renderText(item[column.dataField] as unknown as string, this._getTerms(item)) : '-'}
            </div>
        ));
    }

    private _renderRow = observer((props: ListChildComponentProps<RowData<T>>) => this._renderRowComponent(props));

    private _renderRowComponent({index, data, style}: ListChildComponentProps<RowData<T>>) {
        const item = data.items[index];
        const itemId = this._getId(item);

        return (
            <div
                className={this._getRowClassName(item, itemId, data, index)}
                style={style}
                onClick={() => this._handleRowClick(item, itemId)}
                onMouseEnter={() => this.props.crossTableHoverStore?.setHoveredRowId(itemId)}
                {...this.props.getRowAttributes?.(item)}
            >
                {this._renderRowOverlay(item)}
                {this._renderRowCells(item, data)}
            </div>
        );
    }

    private _renderDynamicRow(item: T, index: number, data: RowData<T>) {
        const itemId = this._getId(item);

        return (
            <div
                key={itemId}
                ref={(el) => { if (el) this._rowRefs.set(itemId, el); else this._rowRefs.delete(itemId); }}
                className={this._getRowClassName(item, itemId, data, index, 'dynamic-row')}
                onClick={() => this._handleRowClick(item, itemId)}
                onMouseEnter={() => this.props.crossTableHoverStore?.setHoveredRowId(itemId)}
                {...this.props.getRowAttributes?.(item)}
            >
                {this._renderRowOverlay(item)}
                {this._renderRowCells(item, data)}
            </div>
        );
    }

    private _renderRowOverlay(item: T) {
        const overlay = this.props.renderRowOverlay?.(item);
        if (!overlay) return null;

        return <div className="data-table-row-overlay">{overlay}</div>;
    }

    private _renderSortOptions({ dataField }: { dataField: keyof T }) {
        const { columnSort } = this._tableFilterStore;
        if (columnSort?.key !== dataField) return null;

        return columnSort.sortType === ColumnSort.ASC ? (
            <div className="sort-item-wrapper active" title={'Sort descending'}>
                <TiArrowSortedUp className="sort-icon" />
            </div>
        ) : (
            <div className="sort-item-wrapper active" title={'Sort ascending'}>
                <TiArrowSortedDown className="sort-icon" />
            </div>
        );
    }

    private _renderHeaderCell(column: DataGridColumnDef<T>, hideRightBorder?: boolean) {
        const { title, dataField, disableSorting, disableFiltering } = column;
        const { columnSort } = this._tableFilterStore;
        const field = (dataField || title?.toLowerCase()) as keyof T;
        const isActive = columnSort?.key === field;
        const nextSort = isActive && columnSort?.sortType === ColumnSort.ASC
            ? ColumnSort.DESC
            : ColumnSort.ASC;
        const isSortingDisabled = !this._items?.length || disableSorting;
        const isFilteringDisabled = !this._items?.length || disableFiltering;

        return (
            <div
                className="header-cell"
                style={{ ...this._getColumnStyle(column), ...(hideRightBorder ? { borderRight: 'none' } : undefined) }}
                key={title}
                ref={(el) => { if (el) this._headerCellRefs.set(field as string, el); }}
            >
                <div
                    className={'header-cell-title'}
                    onClick={(e) => {
                        e.preventDefault();
                        this._changeSortHandler(field as string, nextSort);
                    }}
                >
                    <span>{column.renderHeader ? column.renderHeader() : title}</span>
                    {!isSortingDisabled && this._renderSortOptions({ dataField: field })}
                </div>
                {column.renderSubTitle && (
                    <div className="header-cell-subtitle">{column.renderSubTitle()}</div>
                )}
                {!isFilteringDisabled && this._renderFilterPopup({
                    dataField: field,
                    key: title,
                    dataSource: this.props.initialItemsList
                })}
                <div
                    className="col-resize-handle"
                    onMouseDown={(e) => this._onResizeStart(e, field as string)}
                />
            </div>
        );
    }

    private _renderFilterPopup(cellProps: {dataField: keyof T,
        dataFieldType?: DataFieldType;
        dataSource?: T[];
        text?: string;
        key?: string;
        getFilterOptionName?: (item: T) => string;}) {
        const dataField = cellProps.dataField;
        const {columnFilters, columnSort, columnSearch} = this._tableFilterStore;
        const isFilterActive = this._tableFilterStore?.getIsFilterActive?.(dataField as string);

        return (
            <FilterPopup
                dataField={dataField as string}
                filterOptions={this.filterOptions?.[dataField]??[]}
                hasEmptyValues={this.isColumnHasEmptyValues(dataField as string)}
                isFilterActive={isFilterActive}
                columnSort={columnSort}
                columnFilters={columnFilters}
                columnSearch={columnSearch?.get(dataField as string)??''}
                onChangeSort={this._changeSortHandler}
                onChangeFilters={this._changeFiltersHandler}
                onChangeManyFilters={this._changeManyFiltersHandler}
                onChangeSearch={this._changeSearchHandler}
                onOpen={this._onFilterOpen}
                onClose={this._onFilterClose}
            />
        );
    }

    private _onFilterOpen = (dataField: string) => {
        this._tableFilterStore.setOpenedFilterField(dataField);
    };

    private _onFilterClose = () => {
        this._tableFilterStore.setOpenedFilterField(null);
        this._tableFilterStore.clearColumnSearchFilters();
    };

    private _changeSortHandler = (key: string, sortType: ColumnSort) => {
        this._tableFilterStore?.setColumnSort(key, sortType);
    };

    private _changeFiltersHandler = (key: string, filterValue: FilterValue) => {
        this._tableFilterStore?.setColumnFilters(key, filterValue);
    };

    private _changeManyFiltersHandler = (key: string, filterValues: FilterValue[], add: boolean) => {
        this._tableFilterStore?.setColumnFiltersMany(key, filterValues, add);
    };

    private _changeSearchHandler = (key: string, filterValue: string) => {
        this._tableFilterStore?.setColumnSearch(key, filterValue);
    };

    private _onClearFilters = () => {
        this._tableFilterStore.clearFilters();
    };

    private _getColumnStyle(column: DataGridColumnDef<T>): React.CSSProperties {
        const w = this._columnWidths.get(column.dataField as string);
        return w != null
            ? { width: `${w}px`, flex: '0 0 auto' }
            : { minWidth: column.minWidth, maxWidth: column.maxWidth };
    }

    private _onResizeStart = action((e: React.MouseEvent, field: string) => {
        e.preventDefault();
        e.stopPropagation();

        if (this._columnWidths.size === 0) {
            this._headerCellRefs.forEach((el, key) => {
                this._columnWidths.set(key, el.offsetWidth);
            });
            this._columnWidthsVersion++;
        }

        const currentWidth = this._columnWidths.get(field) ?? this._headerCellRefs.get(field)?.offsetWidth ?? 100;
        this._resizingField = field;
        this._resizeStartX = e.clientX;
        this._resizeStartWidth = currentWidth;
        this._isResizing = true;
        document.addEventListener('mousemove', this._onResizeMove);
        document.addEventListener('mouseup', this._onResizeEnd);
    });

    private _onResizeMove = action((e: MouseEvent) => {
        if (!this._resizingField) return;
        const delta = e.clientX - this._resizeStartX;
        const newWidth = Math.max(55, this._resizeStartWidth + delta);
        const prevWidth = this._columnWidths.get(this._resizingField) ?? this._resizeStartWidth;
        const freed = prevWidth - newWidth;

        if (freed > 0) {
            const columns = this.props.columns;
            const idx = columns.findIndex(c => (c.dataField as string) === this._resizingField);
            if (idx >= 0 && idx < columns.length - 1) {
                const nextField = columns[idx + 1].dataField as string;
                const nextWidth = this._columnWidths.get(nextField) ?? this._headerCellRefs.get(nextField)?.offsetWidth ?? 55;
                this._columnWidths.set(nextField, nextWidth + freed);
            }
        }

        this._columnWidths.set(this._resizingField, newWidth);
        this._columnWidthsVersion++;
    });

    private _onResizeEnd = action(() => {
        this._isResizing = false;
        this._resizingField = null;
        document.removeEventListener('mousemove', this._onResizeMove);
        document.removeEventListener('mouseup', this._onResizeEnd);
        this._tableFilterStore.setColumnWidths(Object.fromEntries(this._columnWidths));
    });

    private _onResetColumnWidths = action(() => {
        this._columnWidths = new Map();
        this._columnWidthsVersion++;
        this._tableFilterStore.resetColumnWidths();
        requestAnimationFrame(() => this._measureHeaderScrollWidth());
    });

    @computed
    private get _totalContentWidth(): number | null {
        if (this._columnWidths.size === 0) return null;
        let total = 0;
        for (const col of this.props.columns) {
            const w = this._columnWidths.get(col.dataField as string);
            if (w == null) return null;
            total += w;
        }
        return total;
    }

    private _getScrollbarWidth(): number {
        return getScrollbarWidth();
    }

    private _getListFilteredByOtherColumns(excludeField: string): T[] {
        return this._applyColumnFilters(this._items, excludeField);
    }

    isColumnHasEmptyValues(dataField: string): boolean {
        const col = this.props.columns.find(c => c.dataField === dataField);
        return this._items.some(item => {
            const value = item[dataField as keyof T];
            if (value === null || value === undefined) return true;
            if (col?.dataFieldType === DataFieldType.Boolean)
                return typeof value === 'number' ? value === 0 : !value;
            if (value instanceof Date) return isNaN(value.getTime());
            if (typeof value === 'string') return value.trim().length === 0;
            return false;
        });
    }
    isValidValue = (val: unknown): val is string | number => {
        if (val === null || val === undefined) return false;

        if (typeof val === 'string') {
            return val.trim().length > 0;
        }

        return typeof val === 'number';
    };

    textComparer: TextMatchComparer = (v, t, s) => {
        return gridStringSearchService.prepareString(v)?.indexOf(gridStringSearchService.prepareString(t) ?? '', s) ?? -1;
    };

    private _matchesFilter(filterVal: FilterValue, itemValue: unknown, dataFieldType?: DataFieldType): boolean {
        if (dataFieldType === DataFieldType.Boolean) {
            const isTruthy = typeof itemValue === 'number' ? itemValue > 0 : !!itemValue;
            return filterVal === 'true' ? isTruthy : !isTruthy;
        }
        if (itemValue instanceof Date) {
            return String(filterVal) === String(itemValue.getTime());
        }
        return String(filterVal) === String(itemValue);
    }
    private _isValuePresent(value: unknown): boolean {
        if (value === null || value === undefined) return false;
        if (value instanceof Date) return !isNaN(value.getTime());
        if (typeof value === 'string') return value.trim().length > 0;
        return true;
    }

    private _applyColumnFilters(list: T[], excludeField?: string): T[] {
        const { columnFilters } = this._tableFilterStore;
        if (!columnFilters?.size) return list;

        return list.filter(item =>
            Array.from(columnFilters.entries()).every(([key, filters]) => {
                if (excludeField && key === excludeField) return true;
                if (!filters?.length) return true;

                const value = item[key as keyof T];
                const colType = this.props.columns.find(c => c.dataField === key)?.dataFieldType;

                const isBooleanPresent = (v: unknown) =>
                    v !== null && v !== undefined && (typeof v === 'number' ? v > 0 : !!v);

                const hasWithValues = filters.includes(ColumnFilters.WithValues);
                const hasWithoutValues = filters.includes(ColumnFilters.WithoutValues);
                const specificFilters = filters.filter(f => f !== ColumnFilters.WithValues && f !== ColumnFilters.WithoutValues);

               if (specificFilters.length > 0) {
                    if (Array.isArray(value)) {
                        const col = this.props.columns.find(c => c.dataField === key);
                        return specificFilters.some(f =>
                            (value as unknown[]).some(el => {
                                if (col?.getFilterItemOptionName) {
                                    return String(f) === col.getFilterItemOptionName(el);
                                }
                                return this._matchesFilter(f, el, colType);
                            })
                        );
                    }
                    return specificFilters.some(f => this._matchesFilter(f, value, colType));
                }

                if (hasWithValues && hasWithoutValues) return true;
                if (hasWithValues)
                    return colType === DataFieldType.Boolean ? isBooleanPresent(value) : this._isValuePresent(value);
                return colType === DataFieldType.Boolean ? !isBooleanPresent(value) : !this._isValuePresent(value);
            })
        );
    }

    private _applyFiltersAndSearch(list: T[]): T[] {
        const { columnFilters, columnSearch } = this._tableFilterStore;
        let result = list;

        if (columnFilters?.size) {
            result = this._applyColumnFilters(result);
        }

        if (columnSearch?.size) {
            result = result.filter(item =>
                Array.from(columnSearch.entries()).every(([key, searchTerm]) => {
                    const terms = gridStringSearchService.splitSearchTerms(searchTerm);
                    if (!terms.length) return true;
                    const value = item[key as keyof T];
                    if (value === null || value === undefined) return false;
                    const normalized = value instanceof Date
                        ? DateTimeService.toUiDate(value)
                        : String(value);
                    return gridStringSearchService.matchesAnyTerm(normalized, terms);
                })
            );
        }

        return result;
    }

    @computed
    get filteredItems(): T[] {
        let list = this._applyFiltersAndSearch(this._items);
        const { columnSort } = this._tableFilterStore;

        if (columnSort) {
            const { key, sortType } = columnSort;
            const col = this.props.columns.find(c => c.dataField === key);

            list = [...list].sort((a, b) => {
                const aRaw = a[key as keyof T];
                const bRaw = b[key as keyof T];

                const aVal = (aRaw instanceof Date ? aRaw.getTime() : aRaw) as number | string;
                const bVal = (bRaw instanceof Date ? bRaw.getTime() : bRaw) as number | string;

                const result = compareValues(aVal, bVal, col?.dataFieldType);
                return sortType === ColumnSort.ASC ? result : -result;
            });
        }

        return list;
    }

   @computed
    get ownFilteredItems(): T[] {
        return this._applyFiltersAndSearch(this.props.initialItemsList);
    }

    @computed
    get filterOptions() {
        if (!this.props.items?.length) return undefined;

        const columns = this.props.columns.map(column => ({
            dataField: column.dataField,
            dataFieldType: column?.dataFieldType,
            getFilterOptionName: column?.getFilterOptionName,
            getFilterItemOptionName: column?.getFilterItemOptionName
        })) as DataGridColumnDef<T>[];

        const options: Partial<Record<keyof T, FilterOption[]>> = {};
        const openedField = this._tableFilterStore.openedFilterField;

        for (const column of columns) {
            const field = column.dataField as keyof T;
            const isDateField = column.dataFieldType === DataFieldType.Date;

            const sourceList = openedField === field
                ? this._getListFilteredByOtherColumns(field)
                : this.props.items;

            if (column.dataFieldType === DataFieldType.Boolean) {
                const boolOpts: FilterOption[] = [];
                const hasTrueValues = sourceList.some(item => {
                    const v = item[field];
                    if (v === null || v === undefined) return false;
                    return typeof v === 'number' ? v > 0 : !!v;
                });
                const hasFalseValues = sourceList.some(item => {
                    const v = item[field];
                    if (v === null || v === undefined) return false;
                    return typeof v === 'number' ? v === 0 : !v;
                });
                if (hasTrueValues) boolOpts.push({name: 'True', value: 'true'});
                if (hasFalseValues) boolOpts.push({name: 'False', value: 'false'});
                options[field] = boolOpts;
                continue;
            }

            if (!sourceList?.length) {
                options[field] = [];
                continue;
            }

            const optionsMap = new Map<string | number, string | number>();

            const addValue = (val: string | number, displayName: string | number) => {
                if (!optionsMap.has(val)) {
                    optionsMap.set(val, displayName);
                }
            };

            for (const item of sourceList) {
                const value = item[field];

                const processValue = (v: unknown) => {
                    if (v instanceof Date) {
                        if (isNaN(v.getTime())) return;
                        const formatted = DateTimeService.toUiDate(v);
                        const key = v.getTime();
                        addValue(key, formatted);
                        return;
                    }

                    if (column.dataFieldType === DataFieldType.Time && typeof v === 'number') {
                        addValue(v, DateTimeService.toUiSeconds(v));
                        return;
                    }

                    if (column.dataFieldType === DataFieldType.Percentage && typeof v === 'number') {
                        const displayName = `${Math.round(v * 100000) / 1000}%`;
                        addValue(v, displayName);
                        return;
                    }

                    if (column.getFilterOptionName) {
                        if (this.isValidValue(v)) {
                            addValue(v, column.getFilterOptionName(item));
                        }
                        return;
                    }

                    if (column.getFilterItemOptionName) {
                        const name = column.getFilterItemOptionName(v);
                        if (name) addValue(name, name);
                        return;
                    }


                    if (this.isValidValue(v)) {
                        addValue(v, v);
                    }
                };
                if (Array.isArray(value)) {
                    value.forEach(processValue);
                } else {
                    processValue(value);
                }
            }

            const arr = Array.from(optionsMap.entries()).map(([value, name]) => ({ value, name }));
            arr.sort((a, b) => compareValues(a.name, b.name, column.dataFieldType));

            const isDateColumn = isDateField || arr.every(opt =>
                typeof opt.name === 'string' && /^\d{2}\.\d{2}\.\d{4}$/.test(opt.name.trim())
            );

            options[field] = isDateColumn ? buildDateGroupOptions(arr) : arr;
        }

        return options;
    }
}
